import { createStore } from 'vuex';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import { getExamAppTokenFromQuery, getExamIdFromQuery } from '@/utils';
import { Log, LogLevel } from '@/interfaces';
import { APP_API_URL } from '@/consts/env';
import exam from './exam';
import session from './session';
import daily from './daily';
import audio from './audio';

export interface State {
  brand: string;
  loading: boolean;
  exam2: never;
  examStarted: boolean;
  originUrl: string;
  exam: any;
  daily: any;
}

const store = createStore({
  modules: {
    exam,
    session,
    daily,
    audio,
  },

  state: () => ({
    brand: 'ICAO4U',
    loading: true,
    exam2: null,
    examStarted: false,
    originUrl: '',
  }),
  actions: {
    setAudioSources({ dispatch }, { sources, log }: { sources: string[], log: (data: Log) => void }) {
      dispatch('audio/setAudioSources', { sources, log });
    },

    replay({ dispatch }, log) {
      dispatch('audio/stopAudio');
      dispatch('audio/playAudio', { current: 0, log });
    },

    next({ commit, dispatch, getters }, log) {
      commit('exam/next');
      dispatch('setAudioSources', { sources: getters['exam/currentView'].audio, log });
    },

    prev({ commit, dispatch, getters }, log) {
      commit('exam/prev');
      dispatch('setAudioSources', { sources: getters['exam/currentView'].audio, log });
    },
    init({ commit }, { state }) {
      commit('exam/init', state);
    },
    initMaster({ commit }, { state }) {
      commit('initMaster', state);
    },
    setOriginUrl({ commit }, { originUrl }) {
      commit('originUrl', originUrl);
    },
    loaded({ commit, dispatch, getters }, log) {
      commit('loaded');
      dispatch('setAudioSources', { sources: getters['exam/currentView'].audio, log });
    },
    setExamStarted({ commit }, examStarted) {
      commit('examStarted', examStarted);
    },
    getUser(_, email) {
      return axios
        .get(`${process.env.MIX_API_URL}get-user?email=${email}`)
        .then(response => response.data)
        .catch(error => {
          console.log(error);
        });
    },
    updateDailyParticipants({ commit }, participants) {
      commit('session/updateDailyParticipants', participants);
    },

    updateSession({ commit }, sessionData) {
      commit('session/updateSession', sessionData);
    },
    updateExaminer({ commit }, examinerData) {
      commit('session/updateExaminer', examinerData);
    },
    updateExamVideo({ commit }, video: MediaStreamTrack) {
      commit('daily/updateExamVideo', video);
    },

    updateStudentVideo({ commit }, video: MediaStreamTrack) {
      commit('daily/updateStudentVideo', video);
    },

    getExamQuestions({ commit, dispatch, rootState }, log: (data: Log) => void) {
      return axios
        .get(`${APP_API_URL}/api/exam-app/exam/${getExamIdFromQuery()}?token=${getExamAppTokenFromQuery()}`)
        .then(response => {
          commit('exam/init', response.data);
          commit('examStarted');
          dispatch('loaded', log);

          const createPreloadImageElement = (image: string) => {
            const img = new Image();
            img.src = image;

            img.onload = () => {
              const linkElement = document.createElement('link');
              linkElement.setAttribute('rel', 'preload');
              linkElement.setAttribute('as', 'image');
              linkElement.setAttribute('href', image);
              document.head.appendChild(linkElement);
            };

            img.onerror = () => {
              log({
                logLevel: LogLevel.ERROR,
                message: `Failed to preload image: ${image}`,
              });
            };
          };

          response.data.steps.forEach((module: any) => {
            if (module?.audio?.length > 0) {
              module.audio.forEach(({ src }: { src: string }) => {
                dispatch('audio/addAudio', { src, log });
              });
            }
            if (module.image) {
              createPreloadImageElement(module.image);
            }

            if (module.questions) {
              if (module.questions?.audio?.length > 0) {
                module.audio.forEach(({ src }: { src: string }) => {
                  dispatch('audio/addAudio', { src, log });
                });
              }

              module.questions.forEach((question: any) => {
                if (question.image) {
                  createPreloadImageElement(question.image);
                }

                if (question?.audio?.length > 0) {
                  question.audio.forEach(({ src }: { src: string }) => {
                    dispatch('audio/addAudio', { src, log });
                  });
                }
              });
            }
          });

          log({
            logLevel: LogLevel.INFO,
            message: 'Preloaded audio object',
            context: { data: cloneDeep(rootState?.['audio']?.audioElements) },
          });
        })
        .catch(error => {
          commit('exam/failed', error.message);
          dispatch('loaded', log);
        });
    },
  },

  mutations: {
    loaded(state): void {
      state.loading = false;
    },
    initMaster(state, payload): void {
      Object.assign(state, payload);
    },
    storeExam(state, data): void {
      state.exam2 = data;
    },
    examStarted(state, value = true): void {
      state.examStarted = value;
    },
    originUrl(state, value = ''): void {
      state.originUrl = value;
    },
  },

  getters: {
    getDelay: (state, getters) => getters['exam/currentModule']?.delay,
    examId: (state, getters) => getters['exam/examId'],
    examStarted: state => state.examStarted,
    results: (state, getters) => getters['exam/results'],
    originUrl: state => state.originUrl,
  },
});

export default store;
